/**
 * Password reset / forgot-password — public auth endpoints.
 * Uses shared axios instance (see ../api.js).
 */
import api from '../api';

/**
 * @param {string} email
 * @param {boolean} isClientPortal - must match the app (admin vs portal)
 * @returns {Promise<{ ok: true, data?: object } | { ok: false, rateLimited?: boolean, message: string }>}
 */
export async function requestPasswordReset(email, isClientPortal) {
  try {
    const { data } = await api.post('/api/auth/forgot-password/', {
      email: String(email ?? '').trim(),
      is_client_portal: !!isClientPortal,
    });
    return { ok: true, data };
  } catch (err) {
    const status = err.response?.status;
    if (status === 429) {
      return {
        ok: false,
        rateLimited: true,
        message: 'Too many requests. Please try again in an hour.',
      };
    }
    return {
      ok: false,
      message: 'Something went wrong. Please try again.',
    };
  }
}

/**
 * @param {string} token - raw token from URL (slashes encoded by backend as needed)
 * @returns {Promise<{ ok: true, valid: boolean, email?: string } | { ok: false, network?: boolean }>}
 */
export async function validateResetToken(token) {
  const safe = encodeURIComponent(String(token ?? '').replace(/\/+$/, ''));
  try {
    const { data } = await api.get(`/api/auth/validate-reset-token/${safe}/`);
    return {
      ok: true,
      valid: !!data?.valid,
      email: typeof data?.email === 'string' ? data.email : '',
    };
  } catch (err) {
    if (!err.response) {
      return { ok: false, network: true };
    }
    return { ok: true, valid: false, email: '' };
  }
}

/**
 * @param {string} token
 * @param {string} newPassword
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function resetPassword(token, newPassword) {
  try {
    const { data } = await api.post('/api/auth/reset-password/', {
      token: String(token ?? '').replace(/\/+$/, ''),
      new_password: newPassword,
    });
    if (data?.success === false && data?.error) {
      return { ok: false, message: String(data.error) };
    }
    if (data?.success === true) {
      return { ok: true };
    }
    return { ok: false, message: 'Could not reset password. Try again or request a new link.' };
  } catch (err) {
    const d = err.response?.data;
    const msg =
      (typeof d?.error === 'string' && d.error) ||
      (typeof d?.detail === 'string' && d.detail) ||
      'Could not reset password. The link may be invalid or expired.';
    return { ok: false, message: msg };
  }
}
